import React, { useContext, useRef } from "react";
import useResizeObserver from "use-resize-observer";
import { BetQLService, BetQLSportItem, PICK_TYPES } from "@rotoql/common-services";
import {
  WEB_SPORTS_MAP,
  // COLUMN_DATE,
  COLUMN_TEAMS,
  COLUMN_CURRENT,
  COLUMN_OPEN,
  COLUMN_VALUE_RATING,
  COLUMN_PUBLIC_TICKET_PCT,
  COLUMN_PUBLIC_MONEY,
  COLUMN_LINE_MOVE_SPREAD,
  COLUMN_INJURIES,
  COLUMN_PRO_MONEY_EDGE,
  COLUMN_TOTAL_GAMES,
  COLUMN_LINE_MOVE_ML,
  COLUMN_LINE_MOVE_OU,
  COLUMN_UNDER_RECORD,
  COLUMN_SEASON_WIN_PCT,
  COLUMN_ATS_RECORD,
  COLUMN_ATS_WIN_PCT,
  COLUMN_OVER_RECORD,
  COLUMN_OVER_PCT,
  COLUMN_UNDER_PCT,
  COLUMN_ROT_NUMBER,
  COLUMN_CURRENT_SPREAD,
  COLUMN_OPEN_SPREAD,
  COLUMN_CURRENT_ML,
  COLUMN_OPEN_ML,
  COLUMN_CURRENT_OU,
  COLUMN_OPEN_OU,
  COLUMN_PUBLIC_TICKET_TOT_SPREAD,
  COLUMN_PUBLIC_TICKET_TOT_ML,
  COLUMN_PUBLIC_TICKET_TOT_OVER,
  COLUMN_PUBLIC_TICKET_TOT_UNDER,
  COLUMN_NET_UNITS,
  COLUMN_ATS_UNITS,
  COLUMN_PITCHERS,
  COLUMN_PTS_FOR,
  COLUMN_PTS_AGAINST,
  COLUMN_HOME_RECORD,
  COLUMN_ROAD_RECORD,
  COLUMN_HOME_ATS_RECORD,
  COLUMN_ROAD_ATS_RECORD,
  COLUMN_HOME_OU_RECORD,
  COLUMN_ROAD_OU_RECORD,
  COLUMN_LINE_MOVE,
  COLUMN_BETQL_MODEL,
  COLUMN_FADE_THE_PUBLIC,
  COLUMN_SHARP_BETTING,
  COLUMN_BETQL_COMMUNITY,
  COLUMN_CONSENSUS,
} from "../../../constants";

import { TrackingProperties } from "../../../constants";
import {
  getBasicCell,
  TeamCell,
  getLineCell,
  getRatingCell,
  getPublicCell,
  getInjuriesCell,
  getSharpCell,
  getPitchersCell,
  getMoneyCell,
  getTrendCell,
  getConsensusCell,
} from "./cells";
import { getGameLeague, getPeriodFilter } from "src/services/EventService";
import styles from "../styles.scss";
import { SubscriptionContext } from "src/services/SubscriptionService";

const { simpleTeamRecordFormat, getOurPick } = BetQLService;

const getMax = (numbers: number[], statIndex: number) => {
  return numbers.indexOf(Math.max(...numbers)) === statIndex;
};

/**
 * This function converts our array of games into an array of columnHeaders to render
 * into the games table.
 *
 * @param columnHeaders
 * @param games
 * @param filterKey
 * @param pageKey
 * @param currentSportObj
 * @param trackingProps
 */
export const getColumns = (
  columnHeaders: any,
  games: any,
  filterKey: string,
  pageKey: string,
  currentSportObj: BetQLSportItem,
  trackingProps: TrackingProperties,
  ads: any[], // [{ component, index }, ...]
  gamesTableRef: any
): any => {
  const { hasSubscriptionAccess } = useContext(SubscriptionContext);
  const periodFilter = getPeriodFilter(filterKey);
  const isNew = currentSportObj[`has${periodFilter}PowerRating`];
  const lineTypeAbbrev: string = PICK_TYPES[filterKey].abbrev;
  const showDraw = currentSportObj.hasDraws && filterKey.includes("moneyline");
  // Iterate through each game and add its columnHeaders JSX into its corresponding
  // column array
  games.forEach((game: any, index: number) => {
    const league = getGameLeague(game);
    const leagueKey = league?.key;
    const hasDraws = league?.hasDraws;
    const trend = game[`max${isNew ? "New" : ""}${periodFilter}Trend`];
    const ourPick = getOurPick(game, pageKey, filterKey, currentSportObj);
    const ourValuePick = getOurPick(game, "value", filterKey, currentSportObj);
    const hasAccess = hasSubscriptionAccess(leagueKey) || game.freeGame;

    const {
      trendsPick: { betqlPick, fadeThePublicPick, proSharpPick, communityPick },
    } = game;

    const isAway = ourPick === "away";
    const isHome = ourPick === "home";
    const isDraw = ourPick === "draw";

    // Team Cell
    columnHeaders[COLUMN_TEAMS].push(
      <TeamCell
        key={index}
        game={game}
        periodFilter={periodFilter}
        pageKey={pageKey}
        currentSportObj={currentSportObj}
        trackingProps={trackingProps}
        trend={trend}
        ourPick={ourPick}
        showDraw={showDraw}
        hasAccess={hasAccess}
      />
    );
    // Date Cell
    // columnHeaders[COLUMN_DATE].push(<DateCell key={index} game={game} />);
    // Current Line Cell
    columnHeaders[COLUMN_CURRENT].push(
      getLineCell({
        game,
        periodFilter,
        pageKey,
        awayStat: game[`current${periodFilter}AwayDisplay`],
        homeStat: game[`current${periodFilter}HomeDisplay`],
        drawStat: game[`current${periodFilter}DrawDisplay`],
        index,
        ourPick,
        className: "games-table-column__current-line-cell",
        showDraw,
        hasAccess,
      })
    );

    // BetQL Model - Trend Bets
    columnHeaders[COLUMN_BETQL_MODEL].push(
      getTrendCell({
        selectedTeam: betqlPick,
        periodFilter,
        game,
        currentSportObj,
        index,
        showDraw,
        hasAccess,
      })
    );

    // Fade the Public
    columnHeaders[COLUMN_FADE_THE_PUBLIC].push(
      getTrendCell({
        selectedTeam: fadeThePublicPick,
        game,
        periodFilter,
        currentSportObj,
        index,
        showDraw,
        hasAccess,
      })
    );

    // Sharp Betting
    columnHeaders[COLUMN_SHARP_BETTING].push(
      getTrendCell({
        selectedTeam: proSharpPick,
        game,
        periodFilter,
        currentSportObj,
        index,
        showDraw,
        hasAccess,
      })
    );

    // BetQL Community
    columnHeaders[COLUMN_BETQL_COMMUNITY].push(
      getTrendCell({
        selectedTeam: communityPick,
        game,
        periodFilter,
        currentSportObj,
        index,
        showDraw,
        hasAccess,
      })
    );

    // Consensus
    columnHeaders[COLUMN_CONSENSUS].push(
      getConsensusCell({
        awayTeam: game.awayTeam,
        homeTeam: game.homeTeam,
        game,
        filterKey,
        periodFilter,
        currentSportObj,
        index,
        showDraw,
        hasAccess,
      })
    );

    // Open Line Cell
    columnHeaders[COLUMN_OPEN].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game[`open${periodFilter}AwayDisplay`],
        homeStat: game[`open${periodFilter}HomeDisplay`],
        drawStat: game[`open${periodFilter}DrawDisplay`],
        awayBold: isAway,
        homeBold: isHome,
        drawBold: isDraw,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        showStatOverride: true,
        showDraw,
      })
    );
    // Rating Cell
    columnHeaders[COLUMN_VALUE_RATING].push(
      getRatingCell(game, index, ourValuePick, filterKey, isNew, trackingProps, currentSportObj, showDraw)
    );
    // Public Percent Cell
    columnHeaders[COLUMN_PUBLIC_TICKET_PCT].push(getPublicCell({ game, index, filterKey, currentSportObj, showDraw }));
    // Money Percent Cell
    columnHeaders[COLUMN_PUBLIC_MONEY].push(
      getMoneyCell(
        game,
        game[`sharp${periodFilter}PublicAwayDisplay`],
        game[`sharp${periodFilter}PublicHomeDisplay`],
        game[`sharp${periodFilter}PublicDrawDisplay`],
        game[`sharp${periodFilter}PublicIsHome`] === false,
        game[`sharp${periodFilter}PublicIsHome`] === true,
        game[`sharp${periodFilter}PublicIsHome`] === null,
        index,
        currentSportObj.key,
        showDraw
      )
    );
    // Line-Move Cell
    columnHeaders[COLUMN_LINE_MOVE].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game[`movement${periodFilter}AwayDifference`],
        homeStat: game[`movement${periodFilter}HomeDifference`],
        drawStat: game[`movement${periodFilter}DrawDifference`],
        awayBold: game[`movement${periodFilter}IsHome`] === false,
        homeBold: game[`movement${periodFilter}IsHome`] === true,
        drawBold: game[`movement${periodFilter}IsHome`] === null,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__line-move-cell",
        trackingProps: {
          source: `Line Move - ${lineTypeAbbrev}`,
        },
        showDraw,
      })
    );
    // FG Spread Line-Move Cell
    columnHeaders[COLUMN_LINE_MOVE_SPREAD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: isAway ? game.movementFGspreadAwayDifference : "",
        homeStat: isHome ? game.movementFGspreadHomeDifference : "",
        awayBold: game.movementFGspreadIsHome === false,
        homeBold: game.movementFGspreadIsHome === true,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__line-move-cell",
        trackingProps: {
          source: "Line Move - Sp",
        },
        showDraw,
      })
    );
    // Injuries Cell
    columnHeaders[COLUMN_INJURIES].push(getInjuriesCell(game, index, showDraw));
    columnHeaders[COLUMN_PRO_MONEY_EDGE].push(
      getSharpCell({
        game,
        index,
        filterKey,
        currentSportObj,
        showDraw,
      })
    );
    // Team Stats Games Cell
    columnHeaders[COLUMN_TOTAL_GAMES].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamGamesDisplay,
        homeStat: game.statHomeTeamGamesDisplay,
        awayBold: false,
        homeBold: false,
        drawBold: false,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Games",
        },
        showDraw,
      })
    );
    // FG Moneyline Line-Move Cell
    columnHeaders[COLUMN_LINE_MOVE_ML].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: isAway ? game.movementFGmoneylineAwayDifference : "",
        homeStat: isHome ? game.movementFGmoneylineHomeDifference : "",
        drawStat: isDraw ? game.movementFGmoneylineDrawDifference : "",
        awayBold: game.movementFGmoneylineDrawDifference === false,
        homeBold: game.movementFGmoneylineDrawDifference === true,
        drawBold: game.movementFGmoneylineDrawDifference === null,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__line-move-cell",
        trackingProps: {
          source: "Line Move - ML",
        },
        showDraw,
      })
    );
    // FG Total Line-Move Cell
    columnHeaders[COLUMN_LINE_MOVE_OU].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: isAway ? game.movementFGtotalAwayDifference : "",
        homeStat: isHome ? game.movementFGtotalHomeDifference : "",
        awayBold: game.movementFGtotalIsHome === false,
        homeBold: game.movementFGtotalIsHome === true,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__line-move-cell",
        trackingProps: {
          source: "Line Move - O/U",
        },
        showDraw,
      })
    );
    // Team Stats Under Record Cell
    columnHeaders[COLUMN_UNDER_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: `${game.statAwayTeamUndersDisplay} - ${game.statAwayTeamOversDisplay} - ${game.statAwayTeamPushesDisplay}`,
        homeStat: `${game.statHomeTeamUndersDisplay} - ${game.statHomeTeamOversDisplay} - ${game.statHomeTeamPushesDisplay}`,
        awayBold: game.statAwayTeamUnderPercentData > game.statHomeTeamUnderPercentData,
        homeBold: game.statHomeTeamUnderPercentData > game.statAwayTeamUnderPercentData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__under-record-cell",
        trackingProps: {
          source: "Under Record",
        },
        showDraw,
      })
    );
    // Team Stats Win Percent Cell
    columnHeaders[COLUMN_SEASON_WIN_PCT].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamWinPercentDisplay,
        homeStat: game.statHomeTeamWinPercentDisplay,
        awayBold:
          game.statAwayTeamWinsData / game.statAwayTeamGamesData >
          game.statHomeTeamWinsData / game.statHomeTeamGamesData,
        homeBold:
          game.statHomeTeamWinsData / game.statHomeTeamGamesData >
          game.statAwayTeamWinsData / game.statAwayTeamGamesData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__season-win-cell",
        trackingProps: {
          source: "Season Win %",
        },
        showDraw,
      })
    );
    // Team Stats ATS Record Cell
    columnHeaders[COLUMN_ATS_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: `${game.statAwayTeamATSWinsDisplay} - ${game.statAwayTeamATSLossesDisplay} - ${game.statAwayTeamATSTiesDisplay}`,
        homeStat: `${game.statHomeTeamATSWinsDisplay} - ${game.statHomeTeamATSLossesDisplay} - ${game.statHomeTeamATSTiesDisplay}`,
        awayBold:
          game.statAwayTeamWinsData / game.statAwayTeamGamesData >
          game.statHomeTeamWinsData / game.statHomeTeamGamesData,
        homeBold:
          game.statHomeTeamWinsData / game.statHomeTeamGamesData >
          game.statAwayTeamWinsData / game.statAwayTeamGamesData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "ATS Rec",
        },
        showDraw,
      })
    );
    // Team Stats ATS Win Percent Cell
    columnHeaders[COLUMN_ATS_WIN_PCT].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamATSWinPercentDisplay,
        homeStat: game.statHomeTeamATSWinPercentDisplay,
        awayBold: game.statAwayTeamATSWinPercentData > game.statHomeTeamATSWinPercentData,
        homeBold: game.statHomeTeamATSWinPercentData > game.statAwayTeamATSWinPercentData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "ATS Win %",
        },
        showDraw,
      })
    );
    // Team Stats Over Record Cell
    columnHeaders[COLUMN_OVER_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: `${game.statAwayTeamOversDisplay} - ${game.statAwayTeamUndersDisplay} - ${game.statAwayTeamPushesDisplay}`,
        homeStat: `${game.statHomeTeamOversDisplay} - ${game.statHomeTeamUndersDisplay} - ${game.statHomeTeamPushesDisplay}`,
        awayBold: game.statAwayTeamOverPercentData > game.statHomeTeamOverPercentData,
        homeBold: game.statHomeTeamOverPercentData > game.statAwayTeamOverPercentData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Over Record",
        },
        showDraw,
      })
    );
    // Team Stats Over Percent Cell
    columnHeaders[COLUMN_OVER_PCT].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamOverPercentDisplay,
        homeStat: game.statHomeTeamOverPercentDisplay,
        awayBold: game.statAwayTeamOverPercentData > game.statHomeTeamOverPercentData,
        homeBold: game.statHomeTeamOverPercentData > game.statAwayTeamOverPercentData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Over %",
        },
        showDraw,
      })
    );
    // Team Stats Under Percent Cell
    columnHeaders[COLUMN_UNDER_PCT].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamUnderPercentDisplay,
        homeStat: game.statHomeTeamUnderPercentDisplay,
        awayBold: game.statAwayTeamUnderPercentData > game.statHomeTeamUnderPercentData,
        homeBold: game.statHomeTeamUnderPercentData > game.statAwayTeamUnderPercentData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Under %",
        },
        showDraw,
      })
    );
    // Rotation Number Cell
    columnHeaders[COLUMN_ROT_NUMBER].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.awayRot || "--",
        homeStat: game.homeRot || "--",
        awayBold: false,
        homeBold: false,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        showStatOverride: true,
        trackingProps: {
          source: "Rot",
        },
        showDraw,
      })
    );
    // Current FG Spread Cell
    columnHeaders[COLUMN_CURRENT_SPREAD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.currentFGspreadAwayDisplay,
        homeStat: game.currentFGspreadHomeDisplay,
        awayBold: isAway,
        homeBold: isHome,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        showStatOverride: true,
        trackingProps: {
          source: "Current - Sp",
        },
        showDraw,
      })
    );
    // Open FG Spread Cell
    columnHeaders[COLUMN_OPEN_SPREAD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.openFGspreadAwayDisplay,
        homeStat: game.openFGspreadHomeDisplay,
        awayBold: isAway,
        homeBold: isHome,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        showStatOverride: true,
        trackingProps: {
          source: "Open - Sp",
        },
        showDraw,
      })
    );
    // Current FG Moneyline Cell
    columnHeaders[COLUMN_CURRENT_ML].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.currentFGmoneylineAwayDisplay,
        homeStat: game.currentFGmoneylineHomeDisplay,
        drawStat: game.currentFGmoneylineDrawDisplay,
        awayBold: isAway,
        homeBold: isHome,
        drawBold: isDraw,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__current-ml-cell",
        showStatOverride: true,
        trackingProps: {
          source: "Current - ML",
        },
        showDraw,
      })
    );
    // Open FG Moneyline Cell
    columnHeaders[COLUMN_OPEN_ML].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.openFGmoneylineAwayDisplay,
        homeStat: game.openFGmoneylineHomeDisplay,
        drawStat: game.openFGmoneylineDrawDisplay,
        awayBold: isAway,
        homeBold: isHome,
        drawBold: isDraw,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        showStatOverride: true,
        trackingProps: {
          source: "Open - ML",
        },
        showDraw,
      })
    );
    // Current FG Total Cell
    columnHeaders[COLUMN_CURRENT_OU].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.currentFGtotalAwayDisplay,
        homeStat: "",
        awayBold: isAway,
        homeBold: isHome,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__current-ou-cell",
        showStatOverride: true,
        trackingProps: {
          source: "Current - O/U",
        },
        showDraw,
      })
    );
    // Open FG Total Cell
    columnHeaders[COLUMN_OPEN_OU].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.openFGtotalAwayDisplay,
        homeStat: "",
        awayBold: isAway,
        homeBold: isHome,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        showStatOverride: true,
        trackingProps: {
          source: "Open - O/U",
        },
        showDraw,
      })
    );
    // FG Spread Sharp Count Cell
    columnHeaders[COLUMN_PUBLIC_TICKET_TOT_SPREAD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.sharpFGspreadCountAwayDisplay,
        homeStat: game.sharpFGspreadCountHomeDisplay,
        awayBold: game.sharpFGspreadCountAwayData > game.sharpFGspreadCountHomeData,
        homeBold: game.sharpFGspreadCountHomeData > game.sharpFGspreadCountAwayData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__ticket-count-spread-cell",
        trackingProps: {
          source: "# of Tickets - Sp",
        },
        showDraw,
      })
    );
    // FG Moneyline Sharp Count Cell
    columnHeaders[COLUMN_PUBLIC_TICKET_TOT_ML].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.sharpFGmoneylineCountAwayDisplay,
        homeStat: game.sharpFGmoneylineCountHomeDisplay,
        drawStat: game.sharpFGmoneylineCountDrawDisplay,
        awayBold: getMax(
          [game.sharpFGmoneylineCountAwayData, game.sharpFGmoneylineCountHomeData, game.sharpFGmoneylineCountDrawData],
          0
        ),
        homeBold: getMax(
          [game.sharpFGmoneylineCountAwayData, game.sharpFGmoneylineCountHomeData, game.sharpFGmoneylineCountDrawData],
          1
        ),
        drawBold: getMax(
          [game.sharpFGmoneylineCountAwayData, game.sharpFGmoneylineCountHomeData, game.sharpFGmoneylineCountDrawData],
          2
        ),
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__ticket-count-ml-cell",
        trackingProps: {
          source: "# of Tickets - ML",
        },
        showDraw,
      })
    );
    // FG Over Sharp Count Cell
    columnHeaders[COLUMN_PUBLIC_TICKET_TOT_OVER].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.sharpFGtotalCountAwayDisplay,
        homeStat: "",
        awayBold: true,
        homeBold: true,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__ticket-count-over-cell",
        trackingProps: {
          source: "# of Tickets - O",
        },
        showDraw,
      })
    );
    // FG Under Sharp Count Cell
    columnHeaders[COLUMN_PUBLIC_TICKET_TOT_UNDER].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.sharpFGtotalCountHomeDisplay,
        homeStat: "",
        awayBold: true,
        homeBold: true,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__ticket-count-under-cell",
        trackingProps: {
          source: "# of Tickets - U",
        },
        showDraw,
      })
    );
    // Team Stats Net Units Cell
    columnHeaders[COLUMN_NET_UNITS].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamNetUnitsDisplay,
        homeStat: game.statHomeTeamNetUnitsDisplay,
        awayBold: game.statAwayTeamNetUnitsData > game.statHomeTeamNetUnitsData,
        homeBold: game.statHomeTeamNetUnitsData > game.statAwayTeamNetUnitsData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Net Units",
        },
        showDraw,
      })
    );
    // Team Stats ATS Units Cell
    columnHeaders[COLUMN_ATS_UNITS].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamATSUnitsDisplay,
        homeStat: game.statHomeTeamATSUnitsDisplay,
        awayBold: game.statAwayTeamATSUnitsData > game.statHomeTeamATSUnitsData,
        homeBold: game.statHomeTeamATSUnitsData > game.statAwayTeamATSUnitsData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "ATS Units",
        },
        showDraw,
      })
    );
    // Pitchers Cell
    columnHeaders[COLUMN_PITCHERS].push(getPitchersCell(game, index));
    // Team Stats Points For Cell
    columnHeaders[COLUMN_PTS_FOR].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamPointsForDisplay,
        homeStat: game.statHomeTeamPointsForDisplay,
        awayBold: game.statAwayTeamPointsForData > game.statHomeTeamPointsForData,
        homeBold: game.statHomeTeamPointsForData > game.statAwayTeamPointsForData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Points For",
        },
        showDraw,
      })
    );
    // Team Stats Points Against Cell
    columnHeaders[COLUMN_PTS_AGAINST].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: game.statAwayTeamPointsAgainstDisplay,
        homeStat: game.statHomeTeamPointsAgainstDisplay,
        awayBold: game.statAwayTeamPointsAgainstData > game.statHomeTeamPointsAgainstData,
        homeBold: game.statHomeTeamPointsAgainstData > game.statAwayTeamPointsAgainstData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        className: "games-table-column__pts-against-cell",
        trackingProps: {
          source: "Points Against",
        },
        showDraw,
      })
    );
    // Home Record Cell
    columnHeaders[COLUMN_HOME_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: simpleTeamRecordFormat({
          wins: game.statAwayTeamHomeWinsDisplay,
          losses: game.statAwayTeamHomeLossesDisplay,
          draws: game.statAwayTeamHomeTiesDisplay,
          hasDraws,
          league: leagueKey,
        }),
        homeStat: simpleTeamRecordFormat({
          wins: game.statHomeTeamHomeWinsDisplay,
          losses: game.statHomeTeamHomeLossesDisplay,
          draws: game.statHomeTeamHomeTiesDisplay,
          hasDraws,
          league: leagueKey,
        }),
        awayBold: game.statAwayTeamHomeWinPercentageData > game.statHomeTeamHomeWinPercentageData,
        homeBold: game.statHomeTeamHomeWinPercentageData > game.statAwayTeamHomeWinPercentageData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Home",
        },
        showDraw,
      })
    );
    // Road Record Cell
    columnHeaders[COLUMN_ROAD_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: simpleTeamRecordFormat({
          wins: game.statAwayTeamRoadWinsDisplay,
          losses: game.statAwayTeamRoadLossesDisplay,
          draws: game.statAwayTeamRoadTiesDisplay,
          hasDraws,
          league: leagueKey,
        }),
        homeStat: simpleTeamRecordFormat({
          wins: game.statHomeTeamRoadWinsDisplay,
          losses: game.statHomeTeamRoadLossesDisplay,
          draws: game.statHomeTeamRoadTiesDisplay,
          hasDraws,
          league: leagueKey,
        }),
        awayBold: game.statAwayTeamRoadWinPercentageData > game.statHomeTeamRoadWinPercentageData,
        homeBold: game.statHomeTeamRoadWinPercentageData > game.statAwayTeamRoadWinPercentageData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Road",
        },
        showDraw,
      })
    );
    // Home ATS Record
    columnHeaders[COLUMN_HOME_ATS_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: `${game.statAwayTeamHomeATSWinsDisplay} - ${game.statAwayTeamHomeATSLossesDisplay} - ${game.statAwayTeamHomeATSTiesDisplay}`,
        homeStat: `${game.statHomeTeamHomeATSWinsDisplay} - ${game.statHomeTeamHomeATSLossesDisplay} - ${game.statHomeTeamHomeATSTiesDisplay}`,
        awayBold: game.statAwayTeamATSWinPercentData > game.statHomeTeamATSWinPercentData,
        homeBold: game.statHomeTeamATSWinPercentData > game.statAwayTeamATSWinPercentData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Home ATS",
        },
        showDraw,
      })
    );
    // Road ATS Record
    columnHeaders[COLUMN_ROAD_ATS_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: `${game.statAwayTeamRoadATSWinsDisplay} - ${game.statAwayTeamRoadATSLossesDisplay} - ${game.statAwayTeamRoadATSTiesDisplay}`,
        homeStat: `${game.statHomeTeamRoadATSWinsDisplay} - ${game.statHomeTeamRoadATSLossesDisplay} - ${game.statHomeTeamRoadATSTiesDisplay}`,
        awayBold: game.statAwayTeamRoadATSWinsPercentageData > game.statHomeTeamRoadATSWinsPercentageData,
        homeBold: game.statHomeTeamRoadATSWinsPercentageData > game.statAwayTeamRoadATSWinsPercentageData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Road ATS",
        },
        showDraw,
      })
    );
    // Home O/U Record
    columnHeaders[COLUMN_HOME_OU_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: `${game.statAwayTeamHomeOUWinsDisplay} - ${game.statAwayTeamHomeOULossesDisplay} - ${game.statAwayTeamHomeOUTiesDisplay}`,
        homeStat: `${game.statHomeTeamHomeOUWinsDisplay} - ${game.statHomeTeamHomeOULossesDisplay} - ${game.statHomeTeamHomeOUTiesDisplay}`,
        awayBold: game.statAwayTeamHomeOUWinsPercentageData > game.statHomeTeamHomeOUWinsPercentageData,
        homeBold: game.statHomeTeamHomeOUWinsPercentageData > game.statAwayTeamHomeOUWinsPercentageData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Home O/U",
        },
        showDraw,
      })
    );
    // Road O/U Record
    columnHeaders[COLUMN_ROAD_OU_RECORD].push(
      getBasicCell({
        pageKey,
        game,
        awayStat: `${game.statAwayTeamRoadOUWinsDisplay} - ${game.statAwayTeamRoadOULossesDisplay} - ${game.statAwayTeamRoadOUTiesDisplay}`,
        homeStat: `${game.statHomeTeamRoadOUWinsDisplay} - ${game.statHomeTeamRoadOULossesDisplay} - ${game.statHomeTeamRoadOUTiesDisplay}`,
        awayBold: game.statAwayTeamRoadOUWinsPercentageData > game.statHomeTeamRoadOUWinsPercentageData,
        homeBold: game.statHomeTeamRoadOUWinsPercentageData > game.statAwayTeamRoadOUWinsPercentageData,
        index,
        sportKey: currentSportObj.key,
        isSharpStat: false,
        trackingProps: {
          source: "Road O/U",
        },
        showDraw,
      })
    );
  });
  const AdPlaceholder = () => <div className="ad-placeholder" />;
  const AdWrapper = ({ children }: any) => {
    const dstRef = useRef<any>();
    useResizeObserver({
      ref: gamesTableRef,
      // NOTE error with "ResizeObserver loop limit exceeded" message (no stack) occurs but I couldn't repro.
      // Should be harmless: https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
      onResize: ({ width }: any) => {
        if (dstRef.current?.style) {
          dstRef.current.style.width = width + "px";
          dstRef.current.style.marginRight = "-2000px";
        }
      },
    });

    return (
      <>
        <div ref={dstRef}>{children}</div>
        <style jsx>{styles}</style>
      </>
    );
  };

  ads.forEach((ad, i) => {
    columnHeaders.forEach((col: any[]) => col.splice(ad.index + 1, 0, <AdPlaceholder key={"ad" + i} />));
    columnHeaders[COLUMN_TEAMS].splice(
      ad.index + 1,
      1,
      <AdWrapper key={"ad" + i}>
        <ad.component />
      </AdWrapper>
    );
  });

  return columnHeaders;
};

interface ColumnOrderLookup {
  [key: number]: number;
}

interface CellEntry {
  id: number;
  cell: JSX.Element;
}

const CONDENSED_CELL_DEFAULT_STYLE: string = "games-table-column__condensed-cell";

const checkAndAdd = (
  lookup: ColumnOrderLookup,
  id: number,
  data: Array<CellEntry>,
  create: (key: number) => JSX.Element
) => {
  if (lookup[id] !== undefined) {
    data.push({
      id,
      cell: create(data.length),
    });
  }
};
/**
 * @param game
 * @param filterKey
 * @param pageKey
 * @param currentSportObj
 * @param trackingProps
 */
export const getCondensedData = (
  game: any,
  filterKey: string,
  pageKey: string,
  columnOrder: number[],
  currentSportObj: BetQLSportItem
): any => {
  const periodFilter = getPeriodFilter(filterKey);
  const showLabel = filterKey === "spread" && WEB_SPORTS_MAP[currentSportObj.url].favoriteLine === "spread";
  const lineTypeAbbrev: string = PICK_TYPES[filterKey].abbrev;
  const fullSpAbbrev = lineTypeAbbrev.toLowerCase() === "sp" ? "Spread" : lineTypeAbbrev;
  const lineTypeLabel = showLabel ? "" : ` - ${lineTypeAbbrev}`;

  const data: Array<CellEntry> = [];
  const columnOrderLookup: ColumnOrderLookup = {};
  for (let i = 0; i < columnOrder.length; i++) {
    columnOrderLookup[columnOrder[i]] = i;
  }

  const ourPick = getOurPick(game, pageKey, filterKey, currentSportObj);
  const isAway = ourPick === "away";
  const isHome = ourPick === "home";
  const league = getGameLeague(game);
  const leagueKey = league?.key;
  const hasDraws = league?.hasDraws;

  // Current Line Cell
  checkAndAdd(columnOrderLookup, COLUMN_CURRENT, data, (key: number) =>
    getLineCell({
      game,
      periodFilter,
      pageKey,
      awayStat: game[`current${periodFilter}AwayDisplay`],
      homeStat: game[`current${periodFilter}HomeDisplay`],
      index: key,
      ourPick,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: `Live - ${fullSpAbbrev}`,
    })
  );

  // Open Line Cell
  checkAndAdd(columnOrderLookup, COLUMN_OPEN, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game[`open${periodFilter}AwayDisplay`],
      homeStat: game[`open${periodFilter}HomeDisplay`],
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: `Open - ${fullSpAbbrev}`,
      showStatOverride: true,
    })
  );

  // Public Percent Cell
  checkAndAdd(columnOrderLookup, COLUMN_PUBLIC_TICKET_PCT, data, (index: number) =>
    getPublicCell({
      game,
      index,
      filterKey,
      currentSportObj,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: `Public Betting %${lineTypeLabel}`,
    })
  );

  // Money Percent Cell
  checkAndAdd(columnOrderLookup, COLUMN_PUBLIC_MONEY, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game[`sharp${periodFilter}PublicAwayDisplay`],
      homeStat: game[`sharp${periodFilter}PublicHomeDisplay`],
      awayBold: !game[`sharp${periodFilter}PublicIsHome`],
      homeBold: game[`sharp${periodFilter}PublicIsHome`],
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: `Public Money %${lineTypeLabel}`,
    })
  );

  // Line-Move Cell
  checkAndAdd(columnOrderLookup, COLUMN_LINE_MOVE, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game[`movement${periodFilter}AwayDifference`],
      homeStat: game[`movement${periodFilter}HomeDifference`],
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: `Line Move${lineTypeLabel}`,
    })
  );

  // FG Spread Line-Move Cell
  checkAndAdd(columnOrderLookup, COLUMN_LINE_MOVE_SPREAD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: isAway ? game.movementFGspreadAwayDifference : "",
      homeStat: isHome ? game.movementFGspreadHomeDifference : "",
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Line Move - Sp",
    })
  );

  checkAndAdd(columnOrderLookup, COLUMN_PRO_MONEY_EDGE, data, (key: number) =>
    getSharpCell({
      game,
      index: key,
      filterKey,
      currentSportObj,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: `Pro Betting %${lineTypeLabel}`,
    })
  );

  // Team Stats Games Cell
  checkAndAdd(columnOrderLookup, COLUMN_TOTAL_GAMES, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamGamesDisplay,
      homeStat: game.statHomeTeamGamesDisplay,
      awayBold: false,
      homeBold: false,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Games",
    })
  );

  // FG Moneyline Line-Move Cell
  checkAndAdd(columnOrderLookup, COLUMN_LINE_MOVE_ML, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: isAway ? game.movementFGmoneylineAwayDifference : "",
      homeStat: isHome ? game.movementFGmoneylineHomeDifference : "",
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Line Move - ML",
    })
  );
  // FG Total Line-Move Cell
  checkAndAdd(columnOrderLookup, COLUMN_LINE_MOVE_OU, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: isAway ? game.movementFGtotalAwayDifference : "",
      homeStat: isHome ? game.movementFGtotalHomeDifference : "",
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Line Move - O/U",
    })
  );

  // Team Stats Under Record Cell
  checkAndAdd(columnOrderLookup, COLUMN_UNDER_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: `${game.statAwayTeamUndersDisplay} - ${game.statAwayTeamOversDisplay} - ${game.statAwayTeamPushesDisplay}`,
      homeStat: `${game.statHomeTeamUndersDisplay} - ${game.statHomeTeamOversDisplay} - ${game.statHomeTeamPushesDisplay}`,
      awayBold: game.statAwayTeamUnderPercentData > game.statHomeTeamUnderPercentData,
      homeBold: game.statHomeTeamUnderPercentData > game.statAwayTeamUnderPercentData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Under Record",
    })
  );
  // Team Stats Win Percent Cell
  checkAndAdd(columnOrderLookup, COLUMN_SEASON_WIN_PCT, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamWinPercentDisplay,
      homeStat: game.statHomeTeamWinPercentDisplay,
      awayBold:
        game.statAwayTeamWinsData / game.statAwayTeamGamesData > game.statHomeTeamWinsData / game.statHomeTeamGamesData,
      homeBold:
        game.statHomeTeamWinsData / game.statHomeTeamGamesData > game.statAwayTeamWinsData / game.statAwayTeamGamesData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Season Win %",
    })
  );
  // Team Stats ATS Record Cell
  checkAndAdd(columnOrderLookup, COLUMN_ATS_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: `${game.statAwayTeamATSWinsDisplay} - ${game.statAwayTeamATSLossesDisplay} - ${game.statAwayTeamATSTiesDisplay}`,
      homeStat: `${game.statHomeTeamATSWinsDisplay} - ${game.statHomeTeamATSLossesDisplay} - ${game.statHomeTeamATSTiesDisplay}`,
      awayBold:
        game.statAwayTeamWinsData / game.statAwayTeamGamesData > game.statHomeTeamWinsData / game.statHomeTeamGamesData,
      homeBold:
        game.statHomeTeamWinsData / game.statHomeTeamGamesData > game.statAwayTeamWinsData / game.statAwayTeamGamesData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "ATS Rec",
    })
  );
  // Team Stats ATS Win Percent Cell
  checkAndAdd(columnOrderLookup, COLUMN_ATS_WIN_PCT, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamATSWinPercentDisplay,
      homeStat: game.statHomeTeamATSWinPercentDisplay,
      awayBold: game.statAwayTeamATSWinPercentData > game.statHomeTeamATSWinPercentData,
      homeBold: game.statHomeTeamATSWinPercentData > game.statAwayTeamATSWinPercentData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "ATS Win %",
    })
  );

  // Team Stats Over Record Cell
  checkAndAdd(columnOrderLookup, COLUMN_OVER_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: `${game.statAwayTeamOversDisplay} - ${game.statAwayTeamUndersDisplay} - ${game.statAwayTeamPushesDisplay}`,
      homeStat: `${game.statHomeTeamOversDisplay} - ${game.statHomeTeamUndersDisplay} - ${game.statHomeTeamPushesDisplay}`,
      awayBold: game.statAwayTeamOverPercentData > game.statHomeTeamOverPercentData,
      homeBold: game.statHomeTeamOverPercentData > game.statAwayTeamOverPercentData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Over Record",
    })
  );

  // Team Stats Over Percent Cell
  checkAndAdd(columnOrderLookup, COLUMN_OVER_PCT, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamOverPercentDisplay,
      homeStat: game.statHomeTeamOverPercentDisplay,
      awayBold: game.statAwayTeamOverPercentData > game.statHomeTeamOverPercentData,
      homeBold: game.statHomeTeamOverPercentData > game.statAwayTeamOverPercentData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Over %",
    })
  );
  // Team Stats Under Percent Cell
  checkAndAdd(columnOrderLookup, COLUMN_UNDER_PCT, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamUnderPercentDisplay,
      homeStat: game.statHomeTeamUnderPercentDisplay,
      awayBold: game.statAwayTeamUnderPercentData > game.statHomeTeamUnderPercentData,
      homeBold: game.statHomeTeamUnderPercentData > game.statAwayTeamUnderPercentData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Under %",
    })
  );
  // Rotation Number Cell
  checkAndAdd(columnOrderLookup, COLUMN_ROT_NUMBER, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.awayRot || "--",
      homeStat: game.homeRot || "--",
      awayBold: false,
      homeBold: false,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Rot",
      showStatOverride: true,
    })
  );
  // Current FG Spread Cell
  checkAndAdd(columnOrderLookup, COLUMN_CURRENT_SPREAD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.currentFGspreadAwayDisplay,
      homeStat: game.currentFGspreadHomeDisplay,
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Live - Spread",
      showStatOverride: true,
    })
  );
  // Open FG Spread Cell
  checkAndAdd(columnOrderLookup, COLUMN_OPEN_SPREAD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.openFGspreadAwayDisplay,
      homeStat: game.openFGspreadHomeDisplay,
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Open - Spread",
      showStatOverride: true,
    })
  );
  // Current FG Moneyline Cell
  checkAndAdd(columnOrderLookup, COLUMN_CURRENT_ML, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.currentFGmoneylineAwayDisplay,
      homeStat: game.currentFGmoneylineHomeDisplay,
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Live - ML",
      showStatOverride: true,
    })
  );
  // Open FG Moneyline Cell
  checkAndAdd(columnOrderLookup, COLUMN_OPEN_ML, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.openFGmoneylineAwayDisplay,
      homeStat: game.openFGmoneylineHomeDisplay,
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Open - ML",
      showStatOverride: true,
    })
  );
  // Current FG Total Cell
  checkAndAdd(columnOrderLookup, COLUMN_CURRENT_OU, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.currentFGtotalAwayDisplay,
      homeStat: "",
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Live - O/U",
      showStatOverride: true,
    })
  );

  // Open FG Total Cell
  checkAndAdd(columnOrderLookup, COLUMN_OPEN_OU, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.openFGtotalAwayDisplay,
      homeStat: "",
      awayBold: isAway,
      homeBold: isHome,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Open - O/U",
      showStatOverride: true,
    })
  );
  // FG Spread Sharp Count Cell
  checkAndAdd(columnOrderLookup, COLUMN_PUBLIC_TICKET_TOT_SPREAD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.sharpFGspreadCountAwayDisplay,
      homeStat: game.sharpFGspreadCountHomeDisplay,
      awayBold: game.sharpFGspreadCountAwayData > game.sharpFGspreadCountHomeData,
      homeBold: game.sharpFGspreadCountHomeData > game.sharpFGspreadCountAwayData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "# of Tickets - Sp",
    })
  );
  // FG Moneyline Sharp Count Cell
  checkAndAdd(columnOrderLookup, COLUMN_PUBLIC_TICKET_TOT_ML, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.sharpFGmoneylineCountAwayDisplay,
      homeStat: game.sharpFGmoneylineCountHomeDisplay,
      awayBold: game.sharpFGmoneylineCountAwayData > game.sharpFGmoneylineCountHomeData,
      homeBold: game.sharpFGmoneylineCountHomeData > game.sharpFGmoneylineCountAwayData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "# of Tickets - ML",
    })
  );
  // FG Over Sharp Count Cell
  checkAndAdd(columnOrderLookup, COLUMN_PUBLIC_TICKET_TOT_OVER, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.sharpFGtotalCountAwayDisplay,
      homeStat: "",
      awayBold: true,
      homeBold: true,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "# of Tickets - O",
    })
  );
  // FG Under Sharp Count Cell
  checkAndAdd(columnOrderLookup, COLUMN_PUBLIC_TICKET_TOT_UNDER, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.sharpFGtotalCountHomeDisplay,
      homeStat: "",
      awayBold: true,
      homeBold: true,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "# of Tickets - U",
    })
  );
  // Team Stats Net Units Cell
  checkAndAdd(columnOrderLookup, COLUMN_NET_UNITS, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamNetUnitsDisplay,
      homeStat: game.statHomeTeamNetUnitsDisplay,
      awayBold: game.statAwayTeamNetUnitsData > game.statHomeTeamNetUnitsData,
      homeBold: game.statHomeTeamNetUnitsData > game.statAwayTeamNetUnitsData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Net Units",
    })
  );
  // Team Stats ATS Units Cell
  checkAndAdd(columnOrderLookup, COLUMN_ATS_UNITS, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamATSUnitsDisplay,
      homeStat: game.statHomeTeamATSUnitsDisplay,
      awayBold: game.statAwayTeamATSUnitsData > game.statHomeTeamATSUnitsData,
      homeBold: game.statHomeTeamATSUnitsData > game.statAwayTeamATSUnitsData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "ATS Units",
    })
  );

  // Team Stats Points For Cell
  checkAndAdd(columnOrderLookup, COLUMN_PTS_FOR, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamPointsForDisplay,
      homeStat: game.statHomeTeamPointsForDisplay,
      awayBold: game.statAwayTeamPointsForData > game.statHomeTeamPointsForData,
      homeBold: game.statHomeTeamPointsForData > game.statAwayTeamPointsForData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Points For",
    })
  );

  // Team Stats Points Against Cell
  checkAndAdd(columnOrderLookup, COLUMN_PTS_AGAINST, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: game.statAwayTeamPointsAgainstDisplay,
      homeStat: game.statHomeTeamPointsAgainstDisplay,
      awayBold: game.statAwayTeamPointsAgainstData > game.statHomeTeamPointsAgainstData,
      homeBold: game.statHomeTeamPointsAgainstData > game.statAwayTeamPointsAgainstData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Points Against",
    })
  );
  // Home Record Cell
  checkAndAdd(columnOrderLookup, COLUMN_HOME_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: simpleTeamRecordFormat({
        wins: game.statAwayTeamHomeWinsDisplay,
        losses: game.statAwayTeamHomeLossesDisplay,
        draws: game.statAwayTeamHomeTiesDisplay,
        hasDraws,
        league: leagueKey,
      }),
      homeStat: simpleTeamRecordFormat({
        wins: game.statHomeTeamHomeWinsDisplay,
        losses: game.statHomeTeamHomeLossesDisplay,
        draws: game.statHomeTeamHomeTiesDisplay,
        hasDraws,
        league: leagueKey,
      }),
      awayBold: game.statAwayTeamHomeWinPercentageData > game.statHomeTeamHomeWinPercentageData,
      homeBold: game.statHomeTeamHomeWinPercentageData > game.statAwayTeamHomeWinPercentageData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Home",
    })
  );
  // Road Record Cell
  checkAndAdd(columnOrderLookup, COLUMN_ROAD_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: simpleTeamRecordFormat({
        wins: game.statAwayTeamRoadWinsDisplay,
        losses: game.statAwayTeamRoadLossesDisplay,
        draws: game.statAwayTeamRoadTiesDisplay,
        hasDraws,
        league: leagueKey,
      }),
      homeStat: simpleTeamRecordFormat({
        wins: game.statHomeTeamRoadWinsDisplay,
        losses: game.statHomeTeamRoadLossesDisplay,
        draws: game.statHomeTeamRoadTiesDisplay,
        hasDraws,
        league: leagueKey,
      }),
      awayBold: game.statAwayTeamRoadWinPercentageData > game.statHomeTeamRoadWinPercentageData,
      homeBold: game.statHomeTeamRoadWinPercentageData > game.statAwayTeamRoadWinPercentageData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Road",
    })
  );
  // Home ATS Record
  checkAndAdd(columnOrderLookup, COLUMN_HOME_ATS_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: `${game.statAwayTeamHomeATSWinsDisplay} - ${game.statAwayTeamHomeATSLossesDisplay} - ${game.statAwayTeamHomeATSTiesDisplay}`,
      homeStat: `${game.statHomeTeamHomeATSWinsDisplay} - ${game.statHomeTeamHomeATSLossesDisplay} - ${game.statHomeTeamHomeATSTiesDisplay}`,
      awayBold: game.statAwayTeamATSWinPercentData > game.statHomeTeamATSWinPercentData,
      homeBold: game.statHomeTeamATSWinPercentData > game.statAwayTeamATSWinPercentData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Home ATS",
    })
  );
  // Road ATS Record
  checkAndAdd(columnOrderLookup, COLUMN_ROAD_ATS_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: `${game.statAwayTeamRoadATSWinsDisplay} - ${game.statAwayTeamRoadATSLossesDisplay} - ${game.statAwayTeamRoadATSTiesDisplay}`,
      homeStat: `${game.statHomeTeamRoadATSWinsDisplay} - ${game.statHomeTeamRoadATSLossesDisplay} - ${game.statHomeTeamRoadATSTiesDisplay}`,
      awayBold: game.statAwayTeamRoadATSWinsPercentageData > game.statHomeTeamRoadATSWinsPercentageData,
      homeBold: game.statHomeTeamRoadATSWinsPercentageData > game.statAwayTeamRoadATSWinsPercentageData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Road ATS",
    })
  );
  // Home O/U Record
  checkAndAdd(columnOrderLookup, COLUMN_HOME_OU_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: `${game.statAwayTeamHomeOUWinsDisplay} - ${game.statAwayTeamHomeOULossesDisplay} - ${game.statAwayTeamHomeOUTiesDisplay}`,
      homeStat: `${game.statHomeTeamHomeOUWinsDisplay} - ${game.statHomeTeamHomeOULossesDisplay} - ${game.statHomeTeamHomeOUTiesDisplay}`,
      awayBold: game.statAwayTeamHomeOUWinsPercentageData > game.statHomeTeamHomeOUWinsPercentageData,
      homeBold: game.statHomeTeamHomeOUWinsPercentageData > game.statAwayTeamHomeOUWinsPercentageData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Home O/U",
    })
  );
  // Road O/U Record
  checkAndAdd(columnOrderLookup, COLUMN_ROAD_OU_RECORD, data, (key: number) =>
    getBasicCell({
      pageKey,
      game,
      awayStat: `${game.statAwayTeamRoadOUWinsDisplay} - ${game.statAwayTeamRoadOULossesDisplay} - ${game.statAwayTeamRoadOUTiesDisplay}`,
      homeStat: `${game.statHomeTeamRoadOUWinsDisplay} - ${game.statHomeTeamRoadOULossesDisplay} - ${game.statHomeTeamRoadOUTiesDisplay}`,
      awayBold: game.statAwayTeamRoadOUWinsPercentageData > game.statHomeTeamRoadOUWinsPercentageData,
      homeBold: game.statHomeTeamRoadOUWinsPercentageData > game.statAwayTeamRoadOUWinsPercentageData,
      index: key,
      sportKey: currentSportObj.key,
      isSharpStat: false,
      className: CONDENSED_CELL_DEFAULT_STYLE,
      fieldName: "Road O/U",
    })
  );

  // Sort it accordingly to it's index ordering as specified by the user
  data.sort((item1: CellEntry, item2: CellEntry) => {
    const order1: number = columnOrderLookup[item1.id];
    const order2: number = columnOrderLookup[item2.id];
    return order1 - order2;
  });

  return data.map((item: CellEntry) => item.cell);
};
